// Local crash / reload recovery for the web build.
//
// A reload, tab eviction, browser crash or power loss would otherwise lose every
// edit made since the last manual save (the `beforeunload` prompt only *warns*).
// This module periodically snapshots the working drawing to IndexedDB — chosen
// over localStorage because a drawing with an embedded PDF is multi-megabyte and
// localStorage is a synchronous ~5 MB store — so startup can offer to restore it.
//
// The renderer (main.ts) owns the app state, so it drives this module: it
// registers a snapshot provider, calls noteRecoveryChange() on every edit
// (debounced here), flushRecovery() on pagehide, clearRecovery() on save/new, and
// reads the drafts once at startup (listAllDrafts) to offer recovery.
//
// Drafts are keyed per tab (see TAB_ID) so two tabs never clobber each other's
// recovery; a startup orphan-scan in main.ts (BroadcastChannel liveness) tells a
// crashed tab's draft apart from one another tab is still editing.

import type { Drawing } from '../shared/types';

export interface RecoverySnapshot {
  drawing: Drawing;
  savedProjectName: string | null;
  cloudRowId: number | null;
  cloudProjectName: string | null;
  savedAt: string; // ISO timestamp of the snapshot
  tabId: string; // the owning tab (also this draft's IndexedDB key)
}

const DB_NAME = 'stenalcad';
const STORE = 'recovery';
const TAB_KEY = 'stenal.tabId';
const DEBOUNCE_MS = 2000;

function genId(): string {
  return (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : String(Math.random()).slice(2);
}

// A stable id for THIS tab, persisted in sessionStorage so it survives a reload
// (the tab recovers its own draft) but is unique per tab (two tabs keep separate
// drafts and never clobber each other). With no sessionStorage we fall back to a
// single shared slot — the pre-multi-tab behaviour, so crash recovery still works.
const TAB_ID: string = (() => {
  try {
    let id = sessionStorage.getItem(TAB_KEY);
    if (!id) { id = genId(); sessionStorage.setItem(TAB_KEY, id); }
    return id;
  } catch { return 'current'; }
})();

/** This tab's recovery id (also the IndexedDB key of its draft). */
export function recoveryTabId(): string { return TAB_ID; }

let dbPromise: Promise<IDBDatabase | null> | null = null;
let snapshotFn: (() => RecoverySnapshot | null) | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let dirtySinceWrite = false; // has the drawing changed since the last persisted snapshot?

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null); // IndexedDB unavailable (private mode / disabled) — degrade silently
    }
  });
  return dbPromise;
}

// Run a readwrite op and resolve when the transaction has actually committed, so a
// following read (or a page unload) sees the durable result. Never rejects.
function mutate(op: (store: IDBObjectStore) => void): Promise<void> {
  return openDb().then((db) => {
    if (!db) return;
    return new Promise<void>((resolve) => {
      try {
        const tx = db.transaction(STORE, 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => resolve();
        tx.onabort = () => resolve();
        op(tx.objectStore(STORE));
      } catch {
        resolve(); // IndexedDB hiccup — the next autosave will retry
      }
    });
  });
}

function writeSnapshot(snap: RecoverySnapshot): Promise<void> {
  return mutate((store) => store.put(snap, TAB_ID));
}

function deleteSnapshot(): Promise<void> {
  return mutate((store) => store.delete(TAB_ID));
}

/** Delete a specific tab's draft (after recovering or discarding an orphan). */
export function deleteDraft(tabId: string): Promise<void> {
  return mutate((store) => store.delete(tabId));
}

/** Register the provider that returns the current snapshot (or null when there is
 *  nothing worth saving — a clean, unmodified drawing). Call once at startup. */
export function initRecovery(fn: () => RecoverySnapshot | null): void {
  snapshotFn = fn;
  void openDb();
}

function persistNow(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  dirtySinceWrite = false;
  if (!snapshotFn) return;
  const snap = snapshotFn();
  if (snap) void writeSnapshot(snap);
  else void deleteSnapshot(); // clean drawing → drop any stale draft
}

/** Note that the drawing changed; writes a snapshot after a short quiet period so
 *  a burst of edits collapses into one write. */
export function noteRecoveryChange(): void {
  if (!snapshotFn) return;
  dirtySinceWrite = true;
  if (timer) clearTimeout(timer);
  timer = setTimeout(persistNow, DEBOUNCE_MS);
}

/** Flush a snapshot immediately (on pagehide / tab hide — more reliable than
 *  beforeunload, especially on mobile). Best-effort: the periodic autosave is the
 *  real guarantee, since an IndexedDB write may not finish during unload. Skips the
 *  write when nothing changed since the last snapshot (avoids rewriting a multi-MB
 *  drawing on every tab-blur). */
export function flushRecovery(): void {
  if (!dirtySinceWrite) return;
  persistNow();
}

/** Discard any stored draft — call after a successful save, New, or open. */
export function clearRecovery(): void {
  if (timer) { clearTimeout(timer); timer = null; }
  dirtySinceWrite = false;
  void deleteSnapshot();
}

/** This tab's own stored draft, if any (the normal same-tab reload case). */
export function readMyRecovery(): Promise<RecoverySnapshot | null> {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise<RecoverySnapshot | null>((resolve) => {
      try {
        const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(TAB_ID);
        req.onsuccess = () => resolve((req.result as RecoverySnapshot) || null);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
  });
}

/** Every stored draft across all tabs (to find orphans left by a crashed tab). */
export function listAllDrafts(): Promise<RecoverySnapshot[]> {
  return openDb().then((db) => {
    if (!db) return [];
    return new Promise<RecoverySnapshot[]>((resolve) => {
      try {
        const req = db.transaction(STORE, 'readonly').objectStore(STORE).getAll();
        req.onsuccess = () => resolve((req.result as RecoverySnapshot[]) || []);
        req.onerror = () => resolve([]);
      } catch {
        resolve([]);
      }
    });
  });
}
